import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";

import { useDesign } from "../../shared/design/DesignProvider";

import {
  createEventCircle,
  getEventCircleDashboard,
  listMyEventCircles,
} from "./eventCircle.api";

import type {
  CreateEventCircleInput,
  EventCircle,
  EventCircleDashboard,
  EventCircleVisibility,
  RecipientType,
} from "./eventCircle.types";

import "./EventCirclePage.css";

type EditorState = {
  title: string;
  description: string;
  location: string;
  startsAt: string;
  endsAt: string;
  visibility: EventCircleVisibility;
  recipientType: RecipientType;
  recipient: string;
};

function emptyEditor(): EditorState {
  const start = new Date();

  start.setHours(
    start.getHours() + 1,
    0,
    0,
    0,
  );

  const end = new Date(start);

  end.setHours(
    end.getHours() + 1,
  );

  return {
    title: "",
    description: "",
    location: "",
    startsAt: toDateTimeLocal(start),
    endsAt: toDateTimeLocal(end),
    visibility: "PRIVATE",
    recipientType: "USERNAME",
    recipient: "",
  };
}

function toDateTimeLocal(date: Date) {
  const corrected = new Date(
    date.getTime() -
      date.getTimezoneOffset() * 60_000,
  );

  return corrected
    .toISOString()
    .slice(0, 16);
}

function eventDate(timestamp: number) {
  return new Intl.DateTimeFormat(
    "en-MY",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
  ).format(new Date(timestamp));
}

function editLabel(count: number) {
  if (count <= 0) {
    return "Original details";
  }

  if (count === 1) {
    return "Edited";
  }

  return `${count} edits`;
}

function errorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

export function EventCirclePage() {
  const {
    plan,
    developer,
    loading: loadingPlan,
  } = useDesign();

  const [circles, setCircles] =
    useState<EventCircle[]>([]);

  const [dashboard, setDashboard] =
    useState<EventCircleDashboard | null>(
      null,
    );

  const [selectedCircleId, setSelectedCircleId] =
    useState("");

  const [editor, setEditor] =
    useState<EditorState>(emptyEditor);

  const [showEditor, setShowEditor] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [shareUrl, setShareUrl] =
    useState("");

  const canHost =
    developer || plan === "PREMIUM_PLUS";

  const selectedCircle = useMemo(
    () =>
      circles.find(
        (circle) =>
          circle.circleId === selectedCircleId,
      ) || null,
    [circles, selectedCircleId],
  );

  async function loadCircles() {
    setLoading(true);
    setError("");

    try {
      const result =
        await listMyEventCircles();

      setCircles(result);

      const firstHostCircle = result.find(
        (circle) => circle.isHost,
      );

      if (
        !selectedCircleId &&
        firstHostCircle
      ) {
        setSelectedCircleId(
          firstHostCircle.circleId,
        );
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  async function loadDashboard(
    circleId: string,
  ) {
    try {
      setDashboard(
        await getEventCircleDashboard(
          circleId,
        ),
      );
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  useEffect(() => {
    void loadCircles();
  }, []);

  useEffect(() => {
    if (
      selectedCircle &&
      selectedCircle.isHost
    ) {
      void loadDashboard(
        selectedCircle.circleId,
      );
    } else {
      setDashboard(null);
    }
  }, [selectedCircle]);

  function updateField<
    Key extends keyof EditorState,
  >(
    key: Key,
    value: EditorState[Key],
  ) {
    setEditor((current) => ({
      ...current,
      [key]: value,
    }));
  }

  async function submit(
    event: FormEvent,
  ) {
    event.preventDefault();

    const startsAt =
      new Date(editor.startsAt).getTime();

    const endsAt =
      new Date(editor.endsAt).getTime();

    if (
      !Number.isFinite(startsAt) ||
      !Number.isFinite(endsAt) ||
      endsAt < startsAt
    ) {
      setError(
        "Choose a valid event start and end time.",
      );
      return;
    }

    if (
      editor.visibility === "PRIVATE" &&
      !editor.recipient.trim()
    ) {
      setError(
        "Choose the username or WhatsApp phone number receiving this private invitation.",
      );
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    const payload: CreateEventCircleInput = {
      title: editor.title,
      description: editor.description,
      location: editor.location,
      startsAt,
      endsAt,
      visibility: editor.visibility,
      recipientType:
        editor.visibility === "PRIVATE"
          ? editor.recipientType
          : undefined,
      recipient:
        editor.visibility === "PRIVATE"
          ? editor.recipient
          : undefined,
    };

    try {
      const created =
        await createEventCircle(payload);

      if (created.token) {
        setShareUrl(
          `${window.location.origin}/invite/${created.token}`,
        );
      }

      setMessage(
        editor.visibility === "PUBLIC"
          ? "Public Event Circle created. Anyone with the link can join."
          : "Private Event Circle created. Only the selected recipient can open the link.",
      );

      setShowEditor(false);
      setEditor(emptyEditor());

      await loadCircles();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  async function copyShareLink() {
    if (!shareUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        shareUrl,
      );

      setMessage("Invitation link copied.");
    } catch {
      setError(
        "Could not copy the link. Select and copy it manually.",
      );
    }
  }

  return (
    <main className="event-circle-page">
      <header className="event-circle-page__header">
        <div>
          <span>SHARED MOMENTS</span>

          <h1>Event Circle</h1>

          <p>
            Plan an event together, track every
            response, and keep everyone updated when
            details change.
          </p>
        </div>

        <button
          className="event-circle-page__create"
          type="button"
          disabled={
            loadingPlan || !canHost
          }
          onClick={() => {
            setError("");
            setMessage("");
            setShowEditor(true);
          }}
        >
          {loadingPlan
            ? "Checking plan…"
            : canHost
              ? "Create Event Circle"
              : "Premium Plus required"}
        </button>
      </header>

      {!loadingPlan && !canHost && (
        <section className="event-circle-page__locked">
          <strong>
            Premium Plus hosts Event Circles.
          </strong>

          <p>
            Anyone can receive an invitation and
            respond. Only the host needs Premium Plus.
          </p>
        </section>
      )}

      {error && (
        <section className="event-circle-page__error">
          {error}
        </section>
      )}

      {message && (
        <section className="event-circle-page__message">
          {message}
        </section>
      )}

      {shareUrl && (
        <section className="event-circle-page__share">
          <div>
            <span>INVITATION LINK</span>

            <strong>
              Send this link to your guest
            </strong>

            <input
              readOnly
              value={shareUrl}
              onFocus={(event) =>
                event.currentTarget.select()
              }
            />
          </div>

          <button
            type="button"
            onClick={() =>
              void copyShareLink()
            }
          >
            Copy link
          </button>
        </section>
      )}

      {loading ? (
        <section className="event-circle-page__empty">
          Loading Event Circles…
        </section>
      ) : circles.length === 0 ? (
        <section className="event-circle-page__empty">
          <span>◌</span>

          <h2>No Event Circles yet</h2>

          <p>
            Your hosted and accepted invitations will
            appear here after login.
          </p>

          {canHost && (
            <button
              type="button"
              onClick={() =>
                setShowEditor(true)
              }
            >
              Create your first Event Circle
            </button>
          )}
        </section>
      ) : (
        <section className="event-circle-page__content">
          <div className="event-circle-page__list">
            {circles.map((circle) => (
              <button
                className={[
                  "event-circle-card",
                  circle.circleId ===
                  selectedCircleId
                    ? "event-circle-card--selected"
                    : "",
                  circle.status === "CANCELLED"
                    ? "event-circle-card--cancelled"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={circle.circleId}
                type="button"
                onClick={() =>
                  setSelectedCircleId(
                    circle.circleId,
                  )
                }
              >
                <span>
                  {circle.isHost
                    ? "HOSTED BY YOU"
                    : "YOUR INVITATION"}
                </span>

                <h2>{circle.title}</h2>

                <p>
                  {eventDate(circle.startsAt)}
                </p>

                <footer>
                  <small>
                    {circle.visibility === "PUBLIC"
                      ? "Public"
                      : "Private"}
                  </small>

                  <small>
                    {editLabel(circle.editCount)}
                  </small>
                </footer>
              </button>
            ))}
          </div>

          {selectedCircle && (
            <aside className="event-circle-detail">
              <span>
                {selectedCircle.isHost
                  ? "HOST DASHBOARD"
                  : "EVENT DETAILS"}
              </span>

              <h2>{selectedCircle.title}</h2>

              <p className="event-circle-detail__date">
                {eventDate(
                  selectedCircle.startsAt,
                )}
              </p>

              {selectedCircle.location && (
                <p>
                  <strong>Location:</strong>{" "}
                  {selectedCircle.location}
                </p>
              )}

              {selectedCircle.description && (
                <p>
                  {selectedCircle.description}
                </p>
              )}

              <div className="event-circle-detail__pills">
                <span>
                  {selectedCircle.visibility ===
                  "PUBLIC"
                    ? "Public event"
                    : "Private event"}
                </span>

                <span>
                  {editLabel(
                    selectedCircle.editCount,
                  )}
                </span>
              </div>

              {dashboard && (
                <section className="event-circle-dashboard">
                  <h3>Guest responses</h3>

                  <div>
                    <article>
                      <strong>
                        {dashboard.going}
                      </strong>

                      <span>Going</span>
                    </article>

                    <article>
                      <strong>
                        {dashboard.maybe}
                      </strong>

                      <span>Maybe</span>
                    </article>

                    <article>
                      <strong>
                        {dashboard.busy}
                      </strong>

                      <span>Busy</span>
                    </article>

                    <article>
                      <strong>
                        {dashboard.notGoing}
                      </strong>

                      <span>Not going</span>
                    </article>

                    <article>
                      <strong>
                        {dashboard.awaiting}
                      </strong>

                      <span>Awaiting</span>
                    </article>
                  </div>
                </section>
              )}
            </aside>
          )}
        </section>
      )}

      {showEditor && (
        <div
          className="event-circle-editor__backdrop"
          role="presentation"
          onMouseDown={() =>
            setShowEditor(false)
          }
        >
          <section
            className="event-circle-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-circle-editor-title"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >
            <header>
              <div>
                <span>PREMIUM PLUS HOST</span>

                <h2
                  id="event-circle-editor-title"
                >
                  New Event Circle
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowEditor(false)
                }
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <form onSubmit={submit}>
              <label>
                Event title

                <input
                  required
                  maxLength={100}
                  value={editor.title}
                  onChange={(event) =>
                    updateField(
                      "title",
                      event.target.value,
                    )
                  }
                />
              </label>

              <label>
                Description

                <textarea
                  rows={3}
                  maxLength={2000}
                  value={editor.description}
                  onChange={(event) =>
                    updateField(
                      "description",
                      event.target.value,
                    )
                  }
                />
              </label>

              <label>
                Location

                <input
                  maxLength={240}
                  value={editor.location}
                  onChange={(event) =>
                    updateField(
                      "location",
                      event.target.value,
                    )
                  }
                />
              </label>

              <div className="event-circle-editor__row">
                <label>
                  Starts

                  <input
                    required
                    type="datetime-local"
                    value={editor.startsAt}
                    onChange={(event) =>
                      updateField(
                        "startsAt",
                        event.target.value,
                      )
                    }
                  />
                </label>

                <label>
                  Ends

                  <input
                    required
                    type="datetime-local"
                    value={editor.endsAt}
                    onChange={(event) =>
                      updateField(
                        "endsAt",
                        event.target.value,
                      )
                    }
                  />
                </label>
              </div>

              <fieldset>
                <legend>Privacy</legend>

                <label>
                  <input
                    type="radio"
                    checked={
                      editor.visibility === "PRIVATE"
                    }
                    onChange={() =>
                      updateField(
                        "visibility",
                        "PRIVATE",
                      )
                    }
                  />

                  Private
                </label>

                <label>
                  <input
                    type="radio"
                    checked={
                      editor.visibility === "PUBLIC"
                    }
                    onChange={() =>
                      updateField(
                        "visibility",
                        "PUBLIC",
                      )
                    }
                  />

                  Public
                </label>
              </fieldset>

              {editor.visibility === "PRIVATE" && (
                <>
                  <label>
                    Send using

                    <select
                      value={
                        editor.recipientType
                      }
                      onChange={(event) =>
                        updateField(
                          "recipientType",
                          event.target
                            .value as RecipientType,
                        )
                      }
                    >
                      <option value="USERNAME">
                        SARADIUM username
                      </option>

                      <option value="PHONE">
                        Verified WhatsApp number
                      </option>
                    </select>
                  </label>

                  <label>
                    {editor.recipientType ===
                    "USERNAME"
                      ? "Recipient username"
                      : "Recipient WhatsApp number"}

                    <input
                      required
                      placeholder={
                        editor.recipientType ===
                        "USERNAME"
                          ? "@username"
                          : "+60123456789"
                      }
                      value={editor.recipient}
                      onChange={(event) =>
                        updateField(
                          "recipient",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                </>
              )}

              <footer>
                <button
                  className="event-circle-editor__cancel"
                  type="button"
                  onClick={() =>
                    setShowEditor(false)
                  }
                >
                  Cancel
                </button>

                <button
                  className="event-circle-editor__save"
                  type="submit"
                  disabled={saving}
                >
                  {saving
                    ? "Creating…"
                    : "Create Event Circle"}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}